/**
 * Per-section skeleton state, shared by every page that loads progressively.
 *
 * The rule: a section shows its skeleton while its data is missing, then crossfades to
 * the real content, once. Where it starts depends on how the page got here, because the
 * skeleton is a BROWSER loading device and nothing else:
 *
 *  - **Server render → 'done'.** The server never emits a skeleton. Whatever data the
 *    render already has (the product payload, static markup) is serialised as real
 *    content, which is what a crawler indexes and what the browser paints before a
 *    single byte of JS has run. Sections the server has no data for serialise empty,
 *    exactly as they did before this system existed.
 *  - **Hydrating first paint → 'idle'.** Content mounted, no shimmer, so the client's
 *    first render matches the server's DOM and hydration has nothing to disagree about.
 *    A section still waiting on data after SK_IDLE_MS falls back to 'pending' and starts
 *    shimmering — a mutation long after hydration has finished, so it is free. Pages
 *    whose data rides in on the SSR transfer state should seed it and resolve() instead,
 *    so nothing shimmers at all (see product-detail's seedFromSsr).
 *  - **Client-side navigation → 'pending'.** No server DOM to match and no data yet, so
 *    the skeleton is up on the first frame, which is the whole point of it.
 *
 * A section walks 'idle'/'pending' → 'crossfade' → 'done'. During 'crossfade' the
 * skeleton and the real content are both in the DOM — the skeleton laid over the content
 * by .sk-layer--out — so the swap has no blank frame and no layout jump. A section
 * revealed straight from 'idle' skips the crossfade: its content was never hidden, and
 * animating it in would be its own flash.
 *
 * Shimmer and crossfade CSS live in the inline <style> in src/index.html (inline so the
 * skeleton is styled the instant the document is parsed, instead of waiting on the
 * render-blocking styles bundle). The timings below must stay in step with the
 * --sk-fade-out custom property there.
 */
export type SkPhase = 'idle' | 'pending' | 'crossfade' | 'done';

/** Gap between consecutive section reveals. Sections fed by one payload would
 *  otherwise pop in on the same frame, which is the "stalls then appears all at
 *  once" effect the cascade exists to avoid. */
export const SK_STAGGER_MS = 90;

/** How long a skeleton stays mounted, overlaid on the content, while it fades
 *  out. Must match --sk-fade-out in the inline <style> in src/index.html. */
export const SK_FADE_OUT_MS = 200;

/** Floor on how long a skeleton stays up, measured from when this state was created.
 *  A very fast response would otherwise clear it within a frame or two, which reads as
 *  a glitch rather than as loading. Usually already satisfied by the time data lands. */
export const SK_MIN_VISIBLE_MS = 400;

/**
 * How long a hydrated section waits, showing the server's content, before it admits it
 * is still loading and starts to shimmer. Kept short — long enough that data already in
 * hand (a transfer-cache hit, a seeded section) never shimmers, short enough that a
 * section the server could not fill doesn't sit there as a blank gap. It also has to
 * land after hydration, so the swap is an ordinary DOM update and not a mismatch.
 */
export const SK_IDLE_MS = 250;

/**
 * How long revealed content plays its entry fade, after which `.sk-content--in` comes
 * back off. Must match --sk-fade-in in the inline <style> in src/index.html, plus a
 * frame or two of slack so a lagging frame can't cut the animation short.
 *
 * The class HAS to be removed, not just left on: it carries an
 * `animation-fill-mode: both`, and an element with a filling animation of `opacity`
 * stays a stacking context for as long as the animation applies — i.e. forever. Any
 * absolutely-positioned popup inside a revealed section then has its z-index trapped in
 * that context and is painted over by whatever section comes next in the DOM (this is
 * what buried the product-detail "Log in to add services" popup under About This Item).
 */
export const SK_FADE_IN_MS = 300;

/**
 * True for the one page that is hydrating the server's HTML, false for every page after
 * it. Three things have to hold, and each rules out a different way of being wrong:
 *
 *  - the app root carries `ng-server-context`, so this document was rendered (or
 *    prerendered) by the server rather than assembled in the browser;
 *  - the route on screen is still the one the document was fetched for. The navigation
 *    timing entry keeps the URL the document was loaded from — pushState doesn't touch
 *    it — so it stays a reliable answer even for a page whose chunk only loads after the
 *    user has clicked through to it;
 *  - nothing has claimed it yet, since only one page can own the markup already painted.
 *
 * Getting it wrong is not fatal either way: a page that wrongly thinks it is hydrating
 * shows empty sections for SK_IDLE_MS before shimmering, and one that wrongly thinks it
 * is not shimmers over content that was already there.
 */
let ssrPaintClaimed = false;
function claimSsrPaint(): boolean {
  if (ssrPaintClaimed) return false;
  if (typeof document === 'undefined' || typeof location === 'undefined') return false;
  if (!document.querySelector('[ng-server-context]')) return false;

  const nav = (performance?.getEntriesByType?.('navigation') || [])[0] as any;
  if (nav?.name) {
    const documentUrl = new URL(nav.name, location.origin);
    if (documentUrl.pathname !== location.pathname || documentUrl.search !== location.search) {
      return false;
    }
  }

  ssrPaintClaimed = true;
  return true;
}

export class SkeletonState<K extends string = string> {
  private phase = new Map<K, SkPhase>();
  /**
   * Per section, when its skeleton went up — drives SK_MIN_VISIBLE_MS. Per section
   * rather than per instance because repend() puts one section back to 'pending' long
   * after the others went up, and it deserves the same floor.
   */
  private startedAt = new Map<K, number>();
  /**
   * Bumped by repend() and resolve(). A reveal timer scheduled before the bump belongs
   * to a load that has since been superseded, so it must not resolve the current one.
   */
  private gen = new Map<K, number>();
  /** Sections whose entry fade has finished, so `.sk-content--in` can come off — see
   *  SK_FADE_IN_MS for why leaving it on is not harmless. */
  private faded = new Set<K>();
  private timers: any[] = [];
  private destroyed = false;

  /**
   * Whether this instance animates. False on the server, which has no crossfade to
   * play — it renders one static frame.
   */
  readonly animate: boolean;

  /** True when this page is hydrating server-rendered markup. */
  readonly hydrating: boolean;

  /**
   * @param keys           section keys, in top-to-bottom page order
   * @param isBrowser      false on the server → no timers, no crossfade
   * @param onChange       called after each phase change (pass cdr.detectChanges)
   * @param clientOnlyData set for a page the server can never fill. Every /account
   *   route is one: the auth token lives in localStorage, so the server runs the guard
   *   without it and serialises the LOG IN page instead. The document still carries
   *   `ng-server-context` for the account URL, so claimSsrPaint() says "hydrating" and
   *   the section sits in 'idle' — content mounted, no shimmer — waiting SK_IDLE_MS on
   *   server content that is not there. That grace period is a blank gap before the
   *   skeleton, which reads as a blink. Such a page still CLAIMS the paint, so no later
   *   page mistakes itself for the hydrating one; it just refuses to wait on it.
   */
  constructor(
    private readonly keys: readonly K[],
    isBrowser: boolean,
    private readonly onChange?: () => void,
    clientOnlyData = false
  ) {
    this.animate = isBrowser;
    const ownsSsrPaint = isBrowser && claimSsrPaint();
    this.hydrating = ownsSsrPaint && !clientOnlyData;
    const start: SkPhase = !isBrowser ? 'done' : this.hydrating ? 'idle' : 'pending';
    const now = Date.now();
    this.keys.forEach((k) => {
      this.phase.set(k, start);
      this.startedAt.set(k, now);
      this.gen.set(k, 0);
      // Content that was never hidden must not play the entry fade.
      if (start !== 'pending') this.faded.add(k);
    });
    if (start === 'idle') this.scheduleIdleFallback();
  }

  /** Skeleton is mounted — waiting on data, or fading out mid-crossfade. */
  show(key: K): boolean {
    const phase = this.phase.get(key);
    return phase === 'pending' || phase === 'crossfade';
  }

  /** Real content is mounted — from the moment the crossfade starts, and from the
   *  first frame on a server render or a hydrated one. */
  ready(key: K): boolean {
    return this.phase.get(key) !== 'pending';
  }

  /** Mid-crossfade: overlay the skeleton so removing it doesn't shift the page. */
  fading(key: K): boolean {
    return this.phase.get(key) === 'crossfade';
  }

  /** Should this section's content play the fade-in? Only in the browser — the server
   *  renders a single frame and an entry animation there would just start life
   *  transparent — and only until the fade has actually finished, after which the class
   *  is dead weight that keeps the section a stacking context (see SK_FADE_IN_MS). */
  fadeIn(key: K): boolean {
    return this.animate && this.ready(key) && !this.faded.has(key);
  }

  /**
   * A hydrated section whose data still hasn't arrived gives up waiting and starts to
   * shimmer. Runs once per key, and only from 'idle' — anything that resolved, revealed
   * or repended in the meantime has bumped its generation and is left alone.
   */
  private scheduleIdleFallback(): void {
    this.keys.forEach((key) => {
      const gen = this.gen.get(key) ?? 0;
      this.timers.push(
        setTimeout(() => {
          if (this.destroyed || this.gen.get(key) !== gen) return;
          if (this.phase.get(key) !== 'idle') return;
          this.phase.set(key, 'pending');
          this.faded.delete(key);
          this.startedAt.set(key, Date.now());
          this.onChange?.();
        }, SK_IDLE_MS)
      );
    });
  }

  /**
   * Content for these sections has arrived — crossfade each in, staggered in the order
   * given, then unmount its skeleton. Sections already revealed are skipped, so a
   * re-fetch doesn't shimmer the page a second time.
   *
   * A NO-OP on the server, which has already rendered every section as content.
   */
  reveal(keys: readonly K[]): void {
    if (!this.animate) return;

    // Still showing the server's content and never shimmered: there is nothing to fade
    // out and nothing to fade in, so just mark those settled.
    this.resolve(keys.filter((k) => this.phase.get(k) === 'idle'));

    const pending = keys.filter((k) => this.phase.get(k) === 'pending');
    const now = Date.now();

    pending.forEach((key, i) => {
      const gen = this.gen.get(key) ?? 0;
      const stale = () => this.destroyed || this.gen.get(key) !== gen;
      const held = Math.max(0, SK_MIN_VISIBLE_MS - (now - (this.startedAt.get(key) ?? now)));
      this.timers.push(
        setTimeout(() => {
          if (stale()) return;
          this.phase.set(key, 'crossfade');
          this.faded.delete(key);
          this.onChange?.();
          this.timers.push(
            setTimeout(() => {
              if (stale()) return;
              this.phase.set(key, 'done');
              this.onChange?.();
            }, SK_FADE_OUT_MS)
          );
          // Entry fade is over: drop `.sk-content--in` so the section stops being a
          // stacking context. Purely a cleanup — the animation has already landed on
          // opacity 1, which is also the value it reverts to.
          this.timers.push(
            setTimeout(() => {
              if (stale()) return;
              this.faded.add(key);
              this.onChange?.();
            }, SK_FADE_IN_MS)
          );
        }, held + i * SK_STAGGER_MS)
      );
    });
  }

  /** Reveal every section still waiting, cascading down the page. */
  revealAll(): void {
    this.reveal(this.keys);
  }

  /**
   * Mark these sections done immediately — no crossfade, no minimum display time. For
   * content that is already known at render time (static markup, seeded data) and for
   * terminal states (e.g. product not found) where no data is coming and a skeleton
   * would never resolve.
   */
  resolve(keys: readonly K[]): void {
    keys.forEach((k) => {
      this.phase.set(k, 'done');
      // Never animated, so it must not carry `.sk-content--in` either.
      this.faded.add(k);
      this.gen.set(k, (this.gen.get(k) ?? 0) + 1);
    });
  }

  /** Drop every remaining skeleton at once. */
  resolveAll(): void {
    this.resolve(this.keys);
  }

  repend(keys: readonly K[]): void {
    if (!this.animate) return;

    const now = Date.now();
    keys.forEach((k) => {
      this.phase.set(k, 'pending');
      this.startedAt.set(k, now);
      this.faded.delete(k);
      this.gen.set(k, (this.gen.get(k) ?? 0) + 1);
    });
    this.onChange?.();
  }

  /** Stop pending reveals from touching a destroyed view. Call from ngOnDestroy. */
  destroy(): void {
    this.destroyed = true;
    this.timers.forEach((t) => clearTimeout(t));
    this.timers = [];
  }
}
